import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import logo from "../../../Assets/Images/logo.png";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import axios from "axios";
import { MAIN_URL } from "../../../Configurations/Urls";
import { Eye, EyeOff } from "lucide-react";
import { setClientPreferredOrganizationId } from "../../../lib/clientActiveOrganization";

function Login({
  title = "User Login",
  appName = "Your App Name",
  description,
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});

  const [loading, setLoading] = useState(false);
  const [pendingSession, setPendingSession] = useState(null);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");

  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // Check if credentials are already saved
    const savedEmail = localStorage.getItem("savedEmail");
    const savedPassword = localStorage.getItem("savedPassword");

    if (savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const { isLoggedIn, login, logout, setAccess } = useAuthStore();
  const navigate = useNavigate();

  const getAllowedOrganizations = (user) => {
    const allOrganizations = user?.organization || [];
    const allowedOrgIds = new Set(
      (user?.metadata || [])
        .map((item) => Number(item?.organization_id))
        .filter(Boolean)
    );

    if (!allowedOrgIds.size) return allOrganizations;
    return allOrganizations.filter((org) =>
      allowedOrgIds.has(Number(org?.organization_id))
    );
  };

  const completeLoginForOrganization = async (organizationId, session = pendingSession) => {
    if (!session?.user || !session?.token) return;

    const response = await axios.post(
      `${MAIN_URL}/api/organizations/extra/active`,
      {
        organization_id: Number(organizationId),
        application_user_id: session.user.application_user_id,
      },
      {
        headers: {
          Authorization: `Bearer ${session.token}`,
        },
      }
    );

    const { user } = response.data;
    const orgId = Number(organizationId);
    const activeOrgId =
      user?.user_active_organization?.organization_id ??
      user?.user_active_organization?.user_current_active_organization_id ??
      orgId;
    const activeOrg = (user?.organization || []).find(
      (item) => Number(item.organization_id) === Number(activeOrgId)
    );
    const mergedOrgUser =
      user?.organizationuser ||
      (Array.isArray(user?.metadata)
        ? user.metadata.find((item) => Number(item?.organization_id) === orgId)
        : null);

    setClientPreferredOrganizationId(orgId);

    login({
      ...user,
      organizations: Array.isArray(user?.organization) ? user.organization : [],
      organization: activeOrg || user?.organization?.[0] || null,
      organizationuser: mergedOrgUser ?? user?.organizationuser ?? null,
      user_active_organization:
        user?.user_active_organization ?? user?.activeOrganization ?? null,
    });
    toast.success("Login successful!");
    setPendingSession(null);
    setSelectedOrganizationId("");
    navigate("/");
  };

  const validateForm = () => {
    let formErrors = {};
    let isValid = true;

    if (!email) {
      formErrors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      formErrors.email = "Invalid email format";
      isValid = false;
    }

    if (!password) {
      formErrors.password = "Password is required";
      isValid = false;
    } else if (password?.length < 6) {
      formErrors.password = "Password must be at least 6 characters";
      isValid = false;
    }

    setErrors(formErrors);
    return isValid;
  };

  const submitLogin = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (rememberMe) {
      localStorage.setItem("savedEmail", email);
      localStorage.setItem("savedPassword", password);
    } else {
      localStorage.removeItem("savedEmail");
      localStorage.removeItem("savedPassword");
    }

    setLoading(true);

 
    try {
      const response = await axios.post(`${MAIN_URL}/api/auth/login`, {
        email,
        password_hash:password,
      });
      const { token, user } = response.data;

      localStorage.setItem("token", token);
      setAccess(user?.role?.system_role_name);
      const organizations = getAllowedOrganizations(user);
      if (organizations.length > 1) {
        setPendingSession({ token, user: { ...user, organization: organizations } });
        setSelectedOrganizationId(
          String(
            organizations.some(
              (o) =>
                Number(o?.organization_id) ===
                Number(user?.user_active_organization?.organization_id)
            )
              ? user?.user_active_organization?.organization_id
              : organizations?.[0]?.organization_id || ""
          )
        );
        toast("Select organization to continue.");
      } else {
        const fallbackOrgId =
          (organizations.some(
            (o) =>
              Number(o?.organization_id) ===
              Number(user?.user_active_organization?.organization_id)
          )
            ? user?.user_active_organization?.organization_id
            : null) ||
          organizations?.[0]?.organization_id;
        await completeLoginForOrganization(fallbackOrgId, {
          token,
          user: { ...user, organization: organizations },
        });
      }
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      const apiMessage =
        typeof data?.message === "string"
          ? data.message
          : Array.isArray(data?.messages)
            ? data.messages.join(" ")
            : null;

      if (data?.errors) {
        toast.error("Please fix the errors below.");
        setErrors(data.errors);
      } else if (apiMessage) {
        toast.error(apiMessage);
      } else if (status === 500) {
        toast.error("Server error occurred. Please try again later.");
      } else if (status === 401 || status === 403) {
        toast.error("Login failed. Please check your credentials or access.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (isLoggedIn && token) {
      navigate("/", { replace: true });
    } else if (isLoggedIn && !token) {
      logout();
    }
  }, [isLoggedIn, logout, navigate]);

  const organizations = pendingSession?.user?.organization || [];

  return (
    <div className="mainSection position-relative">
      <div className="row g-0">
        {/* Left Side (Form) */}
        <div className="col-md-6">
          <div className="whiteBox">
            <div className="panel panel-default">
              <div className="panel-heading text-center">
                <img src={logo} className="img-fluid mb-2" alt="Logo" />
                <h2>{title}</h2>
              </div>
              <div className="panel-body">
                <form onSubmit={submitLogin}>
                  <div className="form-group mb-3">
                    <input
                      placeholder="Enter Your Email"
                      type="text"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`form-control ${errors.email ? "is-invalid" : ""}`}
                    />
                    {errors.email && (
                      <div className="invalid-feedback">{errors.email}</div>
                    )}
                  </div>
                  <div className="form-group mb-3 position-relative">
                    <input
                      placeholder="Enter Password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`form-control pe-5 ${errors.password ? "is-invalid" : ""}`}
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      className="position-absolute top-50 translate-middle-y"
                      style={{
                        right: "15px",
                        cursor: "pointer",
                        color: "#6c757d",
                      }}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </span>
                    {errors.password && (
                      <div className="invalid-feedback">{errors.password}</div>
                    )}
                  </div>

                  <div className="form-check mt-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="rememberMe"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="rememberMe">
                      Remember Password
                    </label>
                  </div>

                  <input
                    type="submit"
                    className="btn btn-primary w-40"
                    value="Login"
                    disabled={loading}
                  />

                    <input
                    type="submit"
                     onClick={() => navigate("/email-register")}
                    className="btn btn-primary w-40"
                    value="Start Free Trial"
                    disabled={loading}
                  />

                  <div className="mt-3">
                    Forgot Password?{" "}
                    <Link to="/forgot-password" className="text-primary">
                      Forgot Password
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side */}
        <div className="col-md-6 d-none d-md-block">
          <div className="darkBox">
            <div className="DarkPanel text-white p-4">
              <h1>{appName}</h1>
              <p>
                {description ||
                  "A powerful and flexible solution to manage your system efficiently."}
              </p>
              <p className="joinedLine mt-3">Trusted by thousands of users.</p>
              <h6 className="mt-4 text-light-emphasis">
                &copy; {new Date().getFullYear()} Your Company Name.
              </h6>
            </div>
          </div>
        </div>
      </div>

      {pendingSession && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.18)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "760px",
              background: "#fff",
              borderRadius: "18px",
              boxShadow: "0 20px 40px rgba(79, 70, 229, 0.15)",
              padding: "28px",
            }}
          >
            <div className="text-center mb-4">
              <img src={logo} className="img-fluid mb-2" alt="Logo" style={{ maxHeight: 48 }} />
              <h3 style={{ marginBottom: 6 }}>Choose Organization</h3>
              <p style={{ color: "#6b7280", marginBottom: 0 }}>
                Select the organization you want to sign in to.
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "12px",
              }}
            >
              {organizations.map((org) => {
                const isSelected =
                  Number(selectedOrganizationId) === Number(org.organization_id);
                return (
                  <button
                    key={org.organization_id}
                    type="button"
                    onClick={() =>
                      setSelectedOrganizationId(String(org.organization_id))
                    }
                    style={{
                      textAlign: "left",
                      borderRadius: "12px",
                      border: isSelected ? "2px solid #4f46e5" : "1px solid #dbe1ff",
                      background: isSelected ? "#eef2ff" : "#fff",
                      padding: "14px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <div style={{ fontWeight: 600, color: "#1f2937" }}>
                      {org.organization_name}
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                      {org.organization_short_name || `Org ID: ${org.organization_id}`}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="d-flex gap-2 justify-content-center mt-4">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                  setPendingSession(null);
                  setSelectedOrganizationId("");
                }}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!selectedOrganizationId || loading}
                onClick={() => completeLoginForOrganization(selectedOrganizationId)}
              >
                {loading ? "Signing in..." : "Continue to Dashboard"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
